import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Assessment as AssessmentIcon,
  TableChart as TableChartIcon,
  Assignment as AssignmentIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon,
  Psychology as PsychologyIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';

interface DataSet {
  id: number;
  appraisalDocumentId: number;
  constructionId: string;
  documentName: string;
  documentIdentifier: string;
  datasetName: string;
  datasetIdentifier: string;
  worksheetName: string;
  startCell: string;
  endCell: string;
  dataDescription?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export const EditDataSetPage: React.FC = () => {
  const navigate = useNavigate();
  const { documentId, dataSetId } = useParams<{ documentId: string; dataSetId: string }>();
  const { authenticatedFetch } = useAuthenticatedFetch();

  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tab state
  const [currentTab, setCurrentTab] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Test Extraction state
  const [extractionLoading, setExtractionLoading] = useState(false);
  const [extractionResults, setExtractionResults] = useState<string>('');
  const [extractionError, setExtractionError] = useState<string | null>(null);

  // Test Explanation state
  const [explanationLoading, setExplanationLoading] = useState(false);
  const [explanationResults, setExplanationResults] = useState<string>('');
  const [explanationError, setExplanationError] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [availableProviders, setAvailableProviders] = useState<string[]>([]);
  const [modelsByProvider, setModelsByProvider] = useState<Record<string, any[]>>({});
  const [userPrompt, setUserPrompt] = useState('');
  const [operationId, setOperationId] = useState<string | null>(null);
  const [pollInterval, setPollInterval] = useState<NodeJS.Timeout | null>(null);

  // Data set data
  const [dataSet, setDataSet] = useState<DataSet | null>(null);
  const [availableWorksheets, setAvailableWorksheets] = useState<string[]>([]);

  // Form fields
  const [datasetName, setDatasetName] = useState('');
  const [selectedWorksheet, setSelectedWorksheet] = useState('');
  const [startCell, setStartCell] = useState('');
  const [endCell, setEndCell] = useState('');
  const [dataDescription, setDataDescription] = useState('');

  // Validation errors
  const [nameError, setNameError] = useState('');
  const [worksheetError, setWorksheetError] = useState('');
  const [startCellError, setStartCellError] = useState('');
  const [endCellError, setEndCellError] = useState('');

  // Check for unsaved changes
  const checkForChanges = () => {
    if (!dataSet) return false;
    // Normalize worksheet names for comparison (null in DB = "First Worksheet in Workbook" in UI)
    const originalWorksheet = dataSet.worksheetName || 'First Worksheet in Workbook';
    return (
      datasetName !== dataSet.datasetName ||
      selectedWorksheet !== originalWorksheet ||
      startCell !== dataSet.startCell ||
      endCell !== dataSet.endCell ||
      dataDescription !== (dataSet.dataDescription || '')
    );
  };

  // Handle tab changes with unsaved changes warning
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const hasChanges = checkForChanges();
    setHasUnsavedChanges(hasChanges);
    setCurrentTab(newValue);
  };

  // Update hasUnsavedChanges when form values change
  useEffect(() => {
    setHasUnsavedChanges(checkForChanges());
  }, [datasetName, selectedWorksheet, startCell, endCell, dataDescription, dataSet]);

  // Handle test extraction
  const handleTestExtraction = async () => {
    if (!dataSet || !documentId || !dataSetId) return;

    setExtractionLoading(true);
    setExtractionError(null);
    setExtractionResults('');

    try {
      const response = await authenticatedFetch(
        `/api/appraisals/admin/documents/${documentId}/datasets/${dataSetId}/test-extraction`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response || !response.ok) {
        const errorData = await response?.json();
        throw new Error(errorData?.message || `Error: ${response?.status || 'Unknown'}`);
      }

      const result = await response.json();
      if (result.success) {
        // Format the extracted data for display
        const formattedData = formatExtractionResults(result);
        setExtractionResults(formattedData);
      } else {
        throw new Error(result.message || 'Failed to extract data');
      }
    } catch (err) {
      console.error('Error during test extraction:', err);
      setExtractionError(err instanceof Error ? err.message : 'Failed to extract data');
    } finally {
      setExtractionLoading(false);
    }
  };

  // Format extraction results for display
  const formatExtractionResults = (result: any) => {
    const { data, rowCount, columnCount, metadata } = result;

    let formatted = `Extraction Results:\n`;
    formatted += `Document: ${metadata.documentName}\n`;
    formatted += `Dataset: ${metadata.datasetName} (${metadata.datasetIdentifier})\n`;
    formatted += `Worksheet: ${metadata.worksheetName}\n`;
    formatted += `Range: ${metadata.cellRange}\n`;
    formatted += `Dimensions: ${rowCount} rows × ${columnCount} columns\n\n`;

    if (data && data.length > 0) {
      formatted += `Data:\n`;
      data.forEach((row: any[], rowIndex: number) => {
        formatted += `Row ${rowIndex + 1}: [${row.map(cell => `"${cell}"`).join(', ')}]\n`;
      });
    } else {
      formatted += `No data extracted from the specified range.\n`;
    }

    return formatted;
  };

  // Handle test explanation
  const handleTestExplanation = async () => {
    if (!dataSet || !documentId || !dataSetId || !userPrompt.trim()) {
      setExplanationError('Please enter a prompt for the AI analysis.');
      return;
    }

    if (!selectedProvider || !selectedModel) {
      setExplanationError('Please select both an LLM provider and model.');
      return;
    }

    setExplanationLoading(true);
    setExplanationError(null);
    setExplanationResults('');

    try {
      const response = await authenticatedFetch(
        `/api/appraisals/admin/documents/${documentId}/datasets/${dataSetId}/test-explanation`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prompt: userPrompt,
            model: selectedModel,
          }),
        }
      );

      if (!response || !response.ok) {
        const errorData = await response?.json();
        throw new Error(errorData?.message || `Error: ${response?.status || 'Unknown'}`);
      }

      const result = await response.json();
      if (result.success) {
        setOperationId(result.operationId);
        setExplanationResults('Processing your request... Please wait while the AI analyzes your data.');

        // Start polling for operation status
        startPolling(result.operationId);
      } else {
        throw new Error(result.message || 'Failed to start explanation');
      }
    } catch (err) {
      console.error('Error during test explanation:', err);
      setExplanationError(err instanceof Error ? err.message : 'Failed to start explanation');
      setExplanationLoading(false);
    }
  };

  // Start polling for operation status
  const startPolling = (opId: string) => {
    const interval = setInterval(async () => {
      try {
        const response = await authenticatedFetch(`/api/operations/${opId}`);

        if (!response || !response.ok) {
          throw new Error('Failed to check operation status');
        }

        const result = await response.json();

        if (result.success) {
          const operation = result.operation;

          if (operation.status === 'completed') {
            // Operation completed successfully
            console.log('Operation completed, result:', operation.result);
            const operationResult = operation.result || {};
            const explanation = operationResult.explanation;
            console.log('Extracted explanation:', explanation);
            setExplanationResults(explanation || 'Analysis completed but no explanation was generated.');
            setExplanationLoading(false);
            clearInterval(interval);
            setPollInterval(null);
          } else if (operation.status === 'failed' || operation.status === 'error') {
            // Operation failed or encountered an error
            console.log('Operation failed/error:', operation.error_message);
            setExplanationError(operation.error_message || 'The AI analysis failed to complete.');
            setExplanationLoading(false);
            clearInterval(interval);
            setPollInterval(null);
          }
          // If status is still 'processing', continue polling
        } else {
          console.error('API returned unsuccessful result:', result);
          throw new Error(result.message || 'Unknown error from operations API');
        }
      } catch (error) {
        console.error('Polling error:', error);
        setExplanationError('Failed to check analysis status.');
        setExplanationLoading(false);
        clearInterval(interval);
        setPollInterval(null);
      }
    }, 5000); // Poll every 5 seconds

    setPollInterval(interval);

    // Set a timeout to stop polling after 5 minutes
    setTimeout(() => {
      if (interval) {
        clearInterval(interval);
        setPollInterval(null);
        if (explanationLoading) {
          setExplanationError('Analysis timed out. Please try again.');
          setExplanationLoading(false);
        }
      }
    }, 5 * 60 * 1000);
  };

  // Handle provider change - update available models
  const handleProviderChange = (provider: string) => {
    setSelectedProvider(provider);
    // Reset model selection when provider changes
    setSelectedModel('');
    // Set first available model for the new provider
    if (modelsByProvider[provider] && modelsByProvider[provider].length > 0) {
      setSelectedModel(modelsByProvider[provider][0].name);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [pollInterval]);

  // Initialize page - fetch data set details and worksheets
  useEffect(() => {
    const initialize = async () => {
      if (!documentId || !dataSetId) {
        setError('Document ID and Data set ID are required');
        setLoading(false);
        return;
      }

      try {
        // Fetch data set details
        const dataSetResponse = await authenticatedFetch(`/api/appraisals/admin/documents/${documentId}/datasets/${dataSetId}`);

        if (!dataSetResponse || !dataSetResponse.ok) {
          const errorData = dataSetResponse ? await dataSetResponse.json() : null;
          throw new Error(errorData?.message || 'Failed to fetch data set details');
        }

        const dataSetResult = await dataSetResponse.json();
        if (!dataSetResult.success || !dataSetResult.dataset) {
          throw new Error(dataSetResult.message || 'Failed to fetch data set details');
        }

        const dataset = dataSetResult.dataset;
        setDataSet(dataset);

        // Fetch worksheet names for this document first
        const worksheetsResponse = await authenticatedFetch(
          `/api/appraisals/construction/xlsx/document/${documentId}/worksheets`
        );

        let worksheets: string[] = [];
        if (worksheetsResponse && worksheetsResponse.ok) {
          const worksheetsResult = await worksheetsResponse.json();
          if (worksheetsResult.success && worksheetsResult.worksheets) {
            worksheets = worksheetsResult.worksheets;
          }
        }

        // If no worksheets in identified_document_worksheets table, use special value
        if (worksheets.length === 0) {
          setAvailableWorksheets(['First Worksheet in Workbook']);
        } else {
          setAvailableWorksheets(worksheets);
        }

        // Populate form fields
        setDatasetName(dataset.datasetName || '');
        // If dataset.worksheetName is null, it means "First Worksheet in Workbook" was used
        setSelectedWorksheet(dataset.worksheetName || 'First Worksheet in Workbook');
        setStartCell(dataset.startCell || '');
        setEndCell(dataset.endCell || '');
        setDataDescription(dataset.dataDescription || '');

      } catch (err) {
        console.error('Error initializing page:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize page');
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [documentId, dataSetId, authenticatedFetch]);

  // Load LLM providers and models
  useEffect(() => {
    const loadLLMOptions = async () => {
      try {
        const response = await authenticatedFetch('/api/appraisals/admin/llm-options');

        if (!response || !response.ok) {
          console.error('Failed to load LLM options');
          return;
        }

        const result = await response.json();
        if (result.success && result.data) {
          setAvailableProviders(result.data.providers || []);
          setModelsByProvider(result.data.models || {});

          // Set default provider and model if available
          if (result.data.providers && result.data.providers.length > 0) {
            const defaultProvider = result.data.providers[0];
            setSelectedProvider(defaultProvider);

            if (result.data.models[defaultProvider] && result.data.models[defaultProvider].length > 0) {
              setSelectedModel(result.data.models[defaultProvider][0].name);
            }
          }
        }
      } catch (error) {
        console.error('Error loading LLM options:', error);
      }
    };

    loadLLMOptions();
  }, [authenticatedFetch]);

  const validateForm = (): boolean => {
    let isValid = true;

    // Reset errors
    setNameError('');
    setWorksheetError('');
    setStartCellError('');
    setEndCellError('');

    // Validate dataset name
    if (!datasetName.trim()) {
      setNameError('Dataset name is required');
      isValid = false;
    } else if (datasetName.length > 100) {
      setNameError('Dataset name must be less than 100 characters');
      isValid = false;
    }

    // Validate worksheet selection
    if (!selectedWorksheet) {
      setWorksheetError('Please select a worksheet');
      isValid = false;
    }

    // Validate cell references
    // Allow * for both cells to indicate "all data on worksheet"
    const bothAreWildcard = startCell.trim() === '*' && endCell.trim() === '*';
    const cellPattern = /^[A-Z]+[0-9]+$/i;

    if (!startCell.trim()) {
      setStartCellError('Start cell is required');
      isValid = false;
    } else if (startCell.trim() !== '*' && !cellPattern.test(startCell)) {
      setStartCellError('Invalid cell format (e.g., A1, B3, or * for entire worksheet)');
      isValid = false;
    }

    if (!endCell.trim()) {
      setEndCellError('End cell is required');
      isValid = false;
    } else if (endCell.trim() !== '*' && !cellPattern.test(endCell)) {
      setEndCellError('Invalid cell format (e.g., T35, Z100, or * for entire worksheet)');
      isValid = false;
    }

    // If one cell is wildcard but not both, show error
    if ((startCell.trim() === '*' && endCell.trim() !== '*') ||
        (startCell.trim() !== '*' && endCell.trim() === '*')) {
      setStartCellError('Both cells must be * to select entire worksheet');
      setEndCellError('Both cells must be * to select entire worksheet');
      isValid = false;
    }

    return isValid;
  };

  const handleSave = async () => {
    if (!validateForm() || !dataSet) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updateData = {
        dataset_name: datasetName.trim(),
        worksheet_name: selectedWorksheet === 'First Worksheet in Workbook' ? null : selectedWorksheet,
        start_cell: startCell.trim().toUpperCase(),
        end_cell: endCell.trim().toUpperCase(),
        data_description: dataDescription.trim() || undefined,
      };

      const response = await authenticatedFetch(
        `/api/appraisals/admin/documents/${documentId}/datasets/${dataSetId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData),
        }
      );

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // Navigate back to document details page
        navigate(`/admin/deliverables/doc-id-system/documents/${documentId}`);
      } else {
        throw new Error(result.message || 'Failed to update data set');
      }
    } catch (err) {
      console.error('Error updating data set:', err);
      setError(err instanceof Error ? err.message : 'Failed to update data set');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (documentId) {
      navigate(`/admin/deliverables/doc-id-system/documents/${documentId}`);
    } else {
      navigate('/admin/deliverables/doc-id-system/documents');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!dataSet) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Data set not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
        >
          <HomeIcon fontSize="inherit" />
          Home
        </Link>
        <Link
          color="inherit"
          href="/admin/deliverables"
          onClick={(e) => {
            e.preventDefault();
            navigate('/admin/deliverables');
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
        >
          <AssessmentIcon fontSize="inherit" />
          Deliverables Admin
        </Link>
        <Link
          color="inherit"
          href="/admin/deliverables/doc-id-system"
          onClick={(e) => {
            e.preventDefault();
            navigate('/admin/deliverables/doc-id-system');
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
        >
          <TableChartIcon fontSize="inherit" />
          Document Identification System
        </Link>
        <Link
          color="inherit"
          href={`/admin/deliverables/doc-id-system/documents/${documentId}`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/admin/deliverables/doc-id-system/documents/${documentId}`);
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
        >
          <TableChartIcon fontSize="inherit" />
          {dataSet.documentName}
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AssignmentIcon fontSize="inherit" />
          {dataSet.datasetName}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1">
            Edit Data Set
          </Typography>
          <Typography variant="body1" color="text.secondary" mt={1}>
            Document: {dataSet.documentName}
          </Typography>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabbed Interface */}
      <Paper sx={{ p: 0 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="dataset edit tabs"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 4, pt: 2 }}
        >
          <Tab
            icon={<InfoIcon />}
            label="Data Set Information"
            id="tab-0"
            aria-controls="tabpanel-0"
          />
          <Tab
            icon={<PlayArrowIcon />}
            label="Test Extraction"
            id="tab-1"
            aria-controls="tabpanel-1"
            disabled={currentTab !== 1 && hasUnsavedChanges}
          />
          <Tab
            icon={<PsychologyIcon />}
            label="Test Explanation"
            id="tab-2"
            aria-controls="tabpanel-2"
            disabled={currentTab !== 2 && hasUnsavedChanges}
          />
        </Tabs>

        {/* Tab Panel 1: Data Set Information */}
        <Box
          role="tabpanel"
          hidden={currentTab !== 0}
          id="tabpanel-0"
          aria-labelledby="tab-0"
          sx={{ p: 4 }}
        >
          {currentTab === 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                Data Set Information
              </Typography>

        <Box component="form" sx={{ mt: 2 }}>
          {/* Dataset Name */}
          <TextField
            fullWidth
            label="Dataset Name"
            value={datasetName}
            onChange={(e) => setDatasetName(e.target.value)}
            error={!!nameError}
            helperText={nameError}
            margin="normal"
            disabled={saving}
          />

          {/* Dataset Identifier (Read-only) */}
          <TextField
            fullWidth
            label="Dataset Identifier"
            value={dataSet.datasetIdentifier}
            margin="normal"
            disabled
            helperText="Dataset identifier cannot be changed"
          />

          {/* Worksheet Selection */}
          <FormControl fullWidth margin="normal" error={!!worksheetError}>
            <InputLabel>Worksheet</InputLabel>
            <Select
              value={selectedWorksheet}
              label="Worksheet"
              onChange={(e) => setSelectedWorksheet(e.target.value)}
              disabled={saving}
            >
              {availableWorksheets.map((worksheet) => (
                <MenuItem key={worksheet} value={worksheet}>
                  {worksheet}
                </MenuItem>
              ))}
            </Select>
            {worksheetError && <FormHelperText>{worksheetError}</FormHelperText>}
          </FormControl>

          {/* Cell Range */}
          <Box display="flex" gap={2} mt={2}>
            <TextField
              label="Start Cell"
              value={startCell}
              onChange={(e) => setStartCell(e.target.value.toUpperCase())}
              error={!!startCellError}
              helperText={startCellError || "e.g., A1 or * for entire worksheet"}
              placeholder="A1 or *"
              disabled={saving}
              sx={{ flex: 1 }}
            />
            <TextField
              label="End Cell"
              value={endCell}
              onChange={(e) => setEndCell(e.target.value.toUpperCase())}
              error={!!endCellError}
              helperText={endCellError || "e.g., T35 or * for entire worksheet"}
              placeholder="T35 or *"
              disabled={saving}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Data Description */}
          <TextField
            fullWidth
            label="Data Description (Optional)"
            value={dataDescription}
            onChange={(e) => setDataDescription(e.target.value)}
            margin="normal"
            multiline
            rows={3}
            disabled={saving}
          />

          {/* Action Buttons */}
          <Box display="flex" gap={2} mt={4}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={saving}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </Box>
        </Box>
            </>
          )}
        </Box>

        {/* Tab Panel 2: Test Extraction */}
        <Box
          role="tabpanel"
          hidden={currentTab !== 1}
          id="tabpanel-1"
          aria-labelledby="tab-1"
          sx={{ p: 4 }}
        >
          {currentTab === 1 && (
            <>
              <Typography variant="h6" gutterBottom>
                Test Extraction
              </Typography>

              {hasUnsavedChanges && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  You have unsaved changes in Tab 1. Please save your changes before testing extraction.
                </Alert>
              )}

              {!hasUnsavedChanges && dataSet && (
                <>
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Current Selection
                    </Typography>
                    <Typography variant="body2">
                      <strong>Worksheet:</strong> {dataSet.worksheetName}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Range:</strong> {dataSet.startCell}:{dataSet.endCell}
                    </Typography>
                  </Box>

                  {extractionError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {extractionError}
                    </Alert>
                  )}

                  <Button
                    variant="contained"
                    startIcon={extractionLoading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                    onClick={handleTestExtraction}
                    disabled={extractionLoading}
                    sx={{ mb: 3 }}
                  >
                    {extractionLoading ? 'Running Test...' : 'Run Test'}
                  </Button>

                  <TextField
                    fullWidth
                    label="Extraction Results"
                    multiline
                    rows={12}
                    value={extractionResults || 'Click "Run Test" to extract data from the Excel file.'}
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{ minHeight: 400, fontFamily: 'monospace' }}
                  />
                </>
              )}
            </>
          )}
        </Box>

        {/* Tab Panel 3: Test Explanation */}
        <Box
          role="tabpanel"
          hidden={currentTab !== 2}
          id="tabpanel-2"
          aria-labelledby="tab-2"
          sx={{ p: 4 }}
        >
          {currentTab === 2 && (
            <>
              <Typography variant="h6" gutterBottom>
                Test Explanation
              </Typography>

              {hasUnsavedChanges && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  You have unsaved changes in Tab 1. Please save your changes before testing explanation.
                </Alert>
              )}

              {!hasUnsavedChanges && dataSet && (
                <>
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Current Selection
                    </Typography>
                    <Typography variant="body2">
                      <strong>Worksheet:</strong> {dataSet.worksheetName}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Range:</strong> {dataSet.startCell}:{dataSet.endCell}
                    </Typography>
                  </Box>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>LLM Provider</InputLabel>
                    <Select
                      value={selectedProvider}
                      label="LLM Provider"
                      onChange={(e) => handleProviderChange(e.target.value)}
                      disabled={explanationLoading}
                    >
                      {availableProviders.map((provider) => (
                        <MenuItem key={provider} value={provider}>
                          {provider.charAt(0).toUpperCase() + provider.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>LLM Model</InputLabel>
                    <Select
                      value={selectedModel}
                      label="LLM Model"
                      onChange={(e) => setSelectedModel(e.target.value)}
                      disabled={explanationLoading || !selectedProvider}
                    >
                      {selectedProvider && modelsByProvider[selectedProvider] ?
                        modelsByProvider[selectedProvider].map((model) => (
                          <MenuItem key={model.name} value={model.name}>
                            {model.displayName || model.name}
                          </MenuItem>
                        )) : []
                      }
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="User Prompt"
                    multiline
                    rows={6}
                    value={userPrompt}
                    onChange={(e) => setUserPrompt(e.target.value)}
                    placeholder="Enter your prompt for the AI to analyze the extracted data..."
                    disabled={explanationLoading}
                    sx={{ mb: 3, minHeight: 200 }}
                  />

                  {explanationError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {explanationError}
                    </Alert>
                  )}

                  <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={explanationLoading ? <CircularProgress size={20} /> : <PsychologyIcon />}
                      onClick={handleTestExplanation}
                      disabled={explanationLoading || !userPrompt.trim() || !selectedProvider || !selectedModel}
                    >
                      {explanationLoading ? 'Analyzing...' : 'Run Test'}
                    </Button>

                    {explanationLoading && operationId && (
                      <Chip
                        label="Processing"
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Box>

                  <TextField
                    fullWidth
                    label="LLM Response"
                    multiline
                    rows={12}
                    value={explanationResults || 'Enter a prompt above and click "Run Test" to get AI-powered analysis of your Excel data.'}
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{ minHeight: 400, fontFamily: 'monospace' }}
                  />
                </>
              )}
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};